import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  Zap,
  Terminal,
  Layers,
  Copy,
  Check,
  ArrowRight,
  Sparkles,
  Lock,
  Globe,
  CheckCircle2,
  TrendingUp,
  AlertTriangle,
  Award,
  Clock,
  DollarSign,
  Briefcase,
  HelpCircle,
  Monitor
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function BingAdsLandingPage() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [activeAngleIndex, setActiveAngleIndex] = useState(0);

  const mcpConfigSnippet = JSON.stringify(
    {
      mcpServers: {
        "upfixe-ads": {
          url: "https://app.upfixe.com/mcp",
        },
      },
    },
    null,
    2
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(mcpConfigSnippet);
    setCopied(true);
    toast({
      title: "MCP Configuration Copied!",
      description: "Paste directly into Claude Desktop or Cursor mcpServers config.",
    });
    setTimeout(() => setCopied(false), 2500);
  };

  const persuasionAngles = [
    {
      id: "risk",
      title: "1. Risk & Loss Prevention",
      subtitle: "The Cost of Inaction for Corporate Decision Makers",
      icon: AlertTriangle,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      mindset: "Corporate buyers on office desktops focused on compliance, preventing budget waste, and mitigating business risk.",
      sampleAd: {
        headline: "Stop Wasting B2B Ad Budget",
        subhead: "Eliminate Overpriced Google CPCs Today",
        description: "Target high-intent corporate buyers on Bing with 35% lower cost-per-click. Direct response certified.",
      }
    },
    {
      id: "authority",
      title: "2. Authority & Certified Trust",
      subtitle: "Enterprise Grade & Proven Track Record",
      icon: Award,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      mindset: "Skeptical enterprise buyers seeking verified partners, security certifications, and dependable architectures.",
      sampleAd: {
        headline: "Enterprise PPC Automation",
        subhead: "Official Microsoft Advertising Partner",
        description: "Direct REST v13 API automation with bank-grade OAuth2 and zero blind spending. Trusted by agencies.",
      }
    },
    {
      id: "aspiration",
      title: "3. Aspiration & Maximum ROI",
      subtitle: "Unlocking Untapped Desktop Arbitrage",
      icon: TrendingUp,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      mindset: "Growth-focused marketers seeking high-spending Windows & Edge users with higher average order values.",
      sampleAd: {
        headline: "Scale Higher-AOV Search Campaigns",
        subhead: "Reach 1.2B Corporate Desktop Users",
        description: "Capitalize on default corporate Edge browsers. Maximize your return on ad spend with 5-angle intent copy.",
      }
    },
    {
      id: "urgency",
      title: "4. Rapid Turnaround & Inquiries",
      subtitle: "Direct Inbound Leads in 24 Hours",
      icon: Clock,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      mindset: "Buyers who need rapid answers, immediate proposal estimates, or fast turnaround times.",
      sampleAd: {
        headline: "Launch Bing Campaigns in 5 Mins",
        subhead: "Fast Turnaround · Instant Setup",
        description: "Input any URL and generate 5 complete Bing ad groups with RSA copy. Published paused for your review.",
      }
    },
    {
      id: "value",
      title: "5. Transparent Value & Lower CPC",
      subtitle: "More Conversions for Less Budget",
      icon: DollarSign,
      color: "text-teal-500",
      bgColor: "bg-teal-500/10",
      borderColor: "border-teal-500/30",
      mindset: "Cost-conscious businesses tired of competitive bidding wars and inflated search prices on Google.",
      sampleAd: {
        headline: "Cut Search Ad Costs by 40%",
        subhead: "Same Buyer Intent · Half The CPC",
        description: "Fixed transparent pricing with zero agency retainers. Start your 7-day free trial on Microsoft Advertising.",
      }
    }
  ];

  const faqs = [
    {
      q: "Why should I advertise on Microsoft Advertising (Bing Ads)?",
      a: "Microsoft Advertising delivers access to over 1.2 billion monthly desktop users across Windows, Microsoft Edge, and Microsoft Copilot. Because Windows is the standard OS in corporate and enterprise environments, Bing traffic features higher average household incomes, stronger B2B purchasing power, and typically 30% to 50% lower Cost Per Click (CPC) compared to Google Ads."
    },
    {
      q: "Do I need a special Microsoft Manager Account or MCC to connect?",
      a: "No! Standard individual or corporate Microsoft Advertising accounts connect seamlessly via our official OAuth2 integration. If you are an agency with multiple accounts, Upfixe supports mapping distinct Customer IDs and Account IDs to each of your brands."
    },
    {
      q: "How does Upfixe prevent accidental budget overspend on Microsoft Ads?",
      a: "Every single campaign, ad group, and keyword generated by Upfixe is pushed to Microsoft Advertising with status PAUSED. Furthermore, ad distribution is locked to OwnedAndOperatedOnly (pure Bing & Yahoo search result pages) so you are never charged for low-converting third-party syndication networks."
    },
    {
      q: "Can I manage Microsoft Advertising directly from Claude Desktop or ChatGPT?",
      a: "Yes! Upfixe includes native Model Context Protocol (MCP) server support. You can ask Claude to research keywords, draft 5-angle copy, publish to Microsoft Advertising, check live performance metrics, and pause campaigns conversationally."
    },
    {
      q: "Can I run campaigns on both Google Ads and Microsoft Ads at the same time?",
      a: "Absolutely. Upfixe is built from the ground up as a dual-network engine. You can create unified brands with both Google and Microsoft credentials, generating synchronized 5-angle campaigns tailored to each network's formatting."
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Upfixe Ads — Microsoft Advertising (Bing Ads) Campaign Generator",
    "operatingSystem": "Web, Claude MCP, ChatGPT MCP",
    "applicationCategory": "BusinessApplication",
    "description": "Generate high-converting 5-angle Microsoft Advertising campaigns on Bing with 30-50% lower CPCs directly from any URL.",
    "url": "https://upfixe.com/bing-ads"
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100 selection:bg-emerald-600 selection:text-white">
      <Helmet>
        <title>Microsoft Advertising (Bing Ads) Automation &amp; AI Campaign Generator | Upfixe</title>
        <meta
          name="description"
          content="Turn any URL into a 5-angle Microsoft Advertising campaign on Bing. Reach 1.2B desktop users with 30-50% lower CPCs. Automated RSA copy, negative keywords, and MCP Claude integration."
        />
        <meta
          name="keywords"
          content="Microsoft Advertising automation, Bing Ads campaign generator, Bing Ads AI tool, Claude desktop Bing ads, Microsoft ads mcp, Bing ads B2B arbitrage, lower CPC ads"
        />
        <meta property="og:title" content="Microsoft Advertising (Bing Ads) Automation | Upfixe" />
        <meta
          property="og:description"
          content="Generate structured 5-angle Microsoft Advertising search campaigns from any URL. 30-50% lower CPCs, published paused for review."
        />
        <link rel="canonical" href="https://upfixe.com/bing-ads" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-28 pb-20 sm:pt-36 sm:pb-28">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(16,185,129,0.2),rgba(255,255,255,0))] pointer-events-none" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span>Microsoft Advertising (Bing Ads) Native Engine</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white max-w-4xl mx-auto leading-tight">
              Turn Any URL Into a 5-Angle{" "}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
                Microsoft Advertising Campaign.
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Why fight over inflated Google CPCs? Reach 1.2 billion monthly desktop searchers across Windows, Edge, and Microsoft Copilot with <strong>30–50% lower CPCs</strong>. Upfixe builds structured ad groups, keywords, and responsive search ads across 5 direct-response persuasion angles — published to Microsoft Advertising as <strong>PAUSED</strong> for your final review.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.upfixe.com/login"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl shadow-emerald-600/25 transition-all"
              >
                Start 7-Day Free Trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
              <Button
                variant="outline"
                size="lg"
                onClick={handleCopy}
                className="w-full sm:w-auto px-6 py-6 text-base border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-slate-300"
              >
                <Terminal className="mr-2 w-4 h-4 text-emerald-400" />
                {copied ? "MCP Config Copied!" : "Connect via Claude / MCP"}
              </Button>
            </div>

            {/* Quick Connect Snippet Card */}
            <div className="mt-12 max-w-2xl mx-auto text-left rounded-xl border border-slate-800 bg-slate-900/90 shadow-2xl p-4 sm:p-6 backdrop-blur">
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-800">
                <div className="flex items-center space-x-2 text-xs font-mono text-slate-400">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/80 inline-block" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/80 inline-block" />
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500/80 inline-block" />
                  <span className="ml-2">claude_desktop_config.json</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="h-8 px-2.5 text-xs text-slate-400 hover:text-white"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                  {copied ? "Copied" : "Copy JSON"}
                </Button>
              </div>
              <pre className="text-xs sm:text-sm font-mono text-emerald-300 overflow-x-auto p-1 leading-relaxed">
                {mcpConfigSnippet}
              </pre>
            </div>
          </div>
        </section>

        {/* Why Bing Ads Arbitrage */}
        <section className="py-20 border-t border-slate-900 bg-slate-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 text-xs mb-3">
                The Desktop Arbitrage Opportunity
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Why High-Performing Brands Run on Microsoft Advertising
              </h2>
              <p className="mt-4 text-slate-400">
                While competitors obsess only over Google Ads, Microsoft Advertising gives you direct access to high-value corporate desktop audiences at a fraction of the cost.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/60 shadow-xl">
                <div className="h-12 w-12 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">30–50% Lower Cost Per Click</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Less auction congestion means your bids go twice as far. Generate qualified B2B and consumer leads without competing against inflated Google budgets.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/60 shadow-xl">
                <div className="h-12 w-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Corporate Purchasing Power</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Windows and Microsoft Edge dominate enterprise corporate networks. Reach professional buyers sitting at work desktops ready to purchase with company cards.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/60 shadow-xl">
                <div className="h-12 w-12 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Bing + Copilot + Yahoo Reach</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Your campaigns distribute across Bing Search, Yahoo Network, DuckDuckGo, and conversational AI answers inside Microsoft Copilot seamlessly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The 5-Angle Persuasion Matrix */}
        <section className="py-24 border-t border-slate-900 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 text-xs mb-3">
                Direct-Response Persuasion Matrix
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                The 5 Buyer Angles Applied to Microsoft Advertising
              </h2>
              <p className="mt-4 text-slate-400">
                Generic ad copy gets ignored. Upfixe structures your Microsoft Advertising ad groups into 5 distinct psychological triggers.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5 space-y-3">
                {persuasionAngles.map((angle, index) => {
                  const Icon = angle.icon;
                  const isActive = activeAngleIndex === index;
                  return (
                    <button
                      key={angle.id}
                      onClick={() => setActiveAngleIndex(index)}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-start space-x-3.5 ${
                        isActive
                          ? `${angle.borderColor} bg-slate-900/90 shadow-lg shadow-emerald-500/5 ring-1 ring-emerald-500/30`
                          : "border-slate-800/80 bg-slate-900/40 hover:bg-slate-900/70 hover:border-slate-700"
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${angle.bgColor} ${angle.color} mt-0.5 flex-shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-base">{angle.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{angle.subtitle}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="lg:col-span-7">
                {(() => {
                  const currentAngle = persuasionAngles[activeAngleIndex];
                  return (
                    <Card className="bg-slate-900/80 border-slate-800 shadow-2xl backdrop-blur">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs border-emerald-500/40 text-emerald-400 bg-emerald-500/10">
                            {currentAngle.title}
                          </Badge>
                          <span className="text-xs text-slate-400 font-mono">Bing RSA Format</span>
                        </div>
                        <CardTitle className="text-2xl text-white mt-2">
                          {currentAngle.sampleAd.headline}
                        </CardTitle>
                        <CardDescription className="text-slate-300 text-sm">
                          {currentAngle.mindset}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="p-4 rounded-lg border border-slate-800 bg-slate-950/60 font-mono text-xs text-slate-300 space-y-2">
                          <div className="text-emerald-400 font-semibold">Headline 1: {currentAngle.sampleAd.headline}</div>
                          <div className="text-blue-400 font-semibold">Headline 2: {currentAngle.sampleAd.subhead}</div>
                          <div className="text-slate-400 mt-2">Description: {currentAngle.sampleAd.description}</div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-emerald-400">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Formatted with standard decimal budget & search-network targeting</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Safety Boundary Section */}
        <section className="py-20 border-t border-slate-900 bg-slate-900/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Built with Strict Safety Guardrails
              </h2>
              <p className="mt-4 text-slate-400">
                Zero surprises. Upfixe respects your ad spend and never risks unreviewed live campaigns.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-slate-900/60 border-slate-800 shadow-xl">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-green-500/10 text-green-400 flex items-center justify-center mb-2">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-xl text-white">Status: PAUSED by Default</CardTitle>
                  <CardDescription className="text-slate-400">
                    Every Microsoft campaign is uploaded in a paused state. You have 100% manual review control before spending a single cent.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-slate-900/60 border-slate-800 shadow-xl">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center mb-2">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-xl text-white">Exact Decimal Currency</CardTitle>
                  <CardDescription className="text-slate-400">
                    Microsoft Advertising uses natural currency units (e.g., 50.00 for $50/day). Upfixe automatically avoids micro-currency rounding errors.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-slate-900/60 border-slate-800 shadow-xl">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center mb-2">
                    <Globe className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-xl text-white">Owned &amp; Operated Only</CardTitle>
                  <CardDescription className="text-slate-400">
                    Targeting is locked to Bing and Yahoo search results, shielding you from low-quality third-party audience syndication waste.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing CTA */}
        <section className="py-20 border-t border-slate-900 bg-slate-950 text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
              Ready to Expand into Microsoft Advertising?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Included with every Upfixe plan. Dual-network Google Ads + Microsoft Advertising with a 7-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.upfixe.com/login"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl shadow-emerald-600/25 transition-all"
              >
                Start Free Trial (Solo $99/mo)
              </a>
              <a
                href="/pricing"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200 transition-all"
              >
                View All Plans
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 border-t border-slate-900 bg-slate-900/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-white">Frequently Asked Questions</h2>
              <p className="mt-3 text-slate-400">Everything you need to know about Microsoft Advertising on Upfixe</p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="p-6 rounded-xl border border-slate-800 bg-slate-900/60">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    {faq.q}
                  </h3>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
